import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { generatePath } from "react-router-dom";
import {
  Camera,
  Check,
  ChevronRight,
  Clock,
  Copy,
  Globe,
  Loader2,
  Plus,
  Mail,
  MapPin,
  Monitor,
  Pencil,
  Shield,
  User as UserIcon,
} from "lucide-react";

import UserAvatar from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CLIENT_OWNER_GROUP,
  ADMIN_GROUP,
  PENTESTER_GROUP,
  CLIENT_OWNER_DISPLAY_VALUE,
  ADMIN_DISPLAY_VALUE,
  PENTESTER_DISPLAY_VALUE,
  ADD_APP_PAGE,
  SINGLE_WEBAPP_PAGE,
  AVATAR_UPLOAD_HINT,
  PREFERRED_USERNAME_LABEL,
} from "@/constants/definitions";
import CognitoLoginClass from "@/models/cognitoLogin";
import UserClass from "@/models/user";
import WebApplicationClass from "@/models/webApplication";
import { formatDateWithLocale } from "@/utils/date";
import { deriveAppAbbrev, deriveAppColor } from "@/utils/appIdentity";
import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";

import { getFileUploadUrl, changeAvatar } from "@/services/graphql/mutations";
import { getGraphData } from "@/utils/transform";
import AvatarCropModal from "@/components/user/AvatarCropModal";
import ChangeUserDetails from "@/components/user/ChangeUserDetails";
import ReplaceAuthenticatorModal from "@/components/user/ReplaceAuthenticatorModal";
import {
  ALLOWED_AVATAR_MIME_TYPES,
  validateAvatarFile,
} from "@/utils/avatarValidation";

// Force Cognito User Pools auth on every GraphQL call.
const graphqlOperation = (query: any, variables?: any) => {
  // Reject any client-side tenantId leak before the request leaves
  // the wrapper. See src/services/graphql/assertNoTenantId.ts for
  // the contract — symmetric to the BE rule "never accept tenantId
  // from client input."
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

// Avatar validation lives in src/utils/avatarValidation.ts so the rules
// (size, MIME allowlist, magic-byte sniff that rejects SVG masquerading
// as PNG, etc.) live in one place. The <input accept> attribute uses
// ALLOWED_AVATAR_MIME_TYPES so the file picker filters to the same set
// the validator enforces.

interface UserPageProps {
  user: UserClass;
  cognitologin: CognitoLoginClass;
  dashboardListOfWebapps: WebApplicationClass[] | null;
  updateCurrentWebAppCallback: (webappid: string) => void;
  navigate: (path: string) => void;
}

function getRoleBadge(roleList: string[]): { label: string; className: string } {
  const roleString = roleList.join(",");
  if (roleString.includes(CLIENT_OWNER_GROUP)) {
    return { label: CLIENT_OWNER_DISPLAY_VALUE, className: "bg-emerald-50 text-emerald-700 ring-emerald-600/20" };
  }
  if (roleString.includes(ADMIN_GROUP)) {
    return { label: ADMIN_DISPLAY_VALUE, className: "bg-blue-50 text-blue-700 ring-blue-600/20" };
  }
  if (roleString.includes(PENTESTER_GROUP)) {
    return { label: PENTESTER_DISPLAY_VALUE, className: "bg-gray-50 text-gray-700 ring-gray-600/20" };
  }
  return { label: "User", className: "bg-gray-50 text-gray-600 ring-gray-500/20" };
}

// How long the "Copied" acknowledgement stays up after a successful copy.
const COPY_ACKNOWLEDGEMENT_MS = 1500;

// IpAddressRow
// -------------
// One IP address in the Security & Activity grid.
//
// The address is whatever Cognito recorded for the auth event, so it may be
// IPv6 and up to 45 characters. Two decisions follow from that:
//   * it is truncated to a single line rather than wrapped, because a hard-
//     wrapped address is harder to read than an elided one, and the full value
//     is available on hover and via Copy.
//   * it uses tabular-nums in the page's own font rather than a monospace
//     face. Monospace on these two values alone was the only place on the page
//     using a different typeface.
function IpAddressRow({ label, ipAddress }: { label: string; ipAddress: string }) {
  const [hasCopied, setHasCopied] = useState(false);

  useEffect(() => {
    if (!hasCopied) return;
    const acknowledgementTimer = window.setTimeout(() => setHasCopied(false), COPY_ACKNOWLEDGEMENT_MS);
    return () => window.clearTimeout(acknowledgementTimer);
  }, [hasCopied]);

  const canCopy = ipAddress !== "N/A";

  const handleCopy = async () => {
    // navigator.clipboard is undefined outside a secure context. Nothing here
    // is critical, so a failure just leaves the value on screen to select.
    try {
      await navigator.clipboard?.writeText(ipAddress);
      setHasCopied(true);
    } catch (copyError) {
      console.error("Could not copy the IP address to the clipboard");
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className="rounded-lg bg-gray-100 p-2">
        <Monitor className="h-4 w-4 text-gray-500" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <span className="truncate text-base tabular-nums" title={ipAddress}>
            {ipAddress}
          </span>
          {canCopy && (
            <button
              type="button"
              onClick={handleCopy}
              aria-label={`Copy ${label.toLowerCase()} address`}
              className="shrink-0 rounded p-1 text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-500"
            >
              {hasCopied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
        {hasCopied && <span className="sr-only" role="status">Copied</span>}
      </div>
    </div>
  );
}

export default function UserPage({
  user,
  cognitologin,
  dashboardListOfWebapps,
  updateCurrentWebAppCallback,
  navigate,
}: UserPageProps) {
  const [avatarSrc, setAvatarSrc] = useState(user.avatar);
  const [uploading, setUploading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  // Chosen and validated, waiting for the user to position it before upload.
  const [fileAwaitingCrop, setFileAwaitingCrop] = useState<File | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  // Bumped when the edit dialog persists a change. The user model is a mutable
  // class instance, so React needs a state change to re-render the new values.
  const [, setProfileRevision] = useState(0);
  // The user's active MFA method. "SOFTWARE_TOKEN_MFA" once TOTP is enrolled;
  // drives whether the "Replace authenticator" affordance appears (so it stays
  // hidden while the pool has MFA switched off — every user reads "NOMFA" then).
  const [mfaMethod, setMfaMethod] = useState("NOMFA");
  const [showReplaceMfa, setShowReplaceMfa] = useState(false);

  useEffect(() => {
    let cancelled = false;
    cognitologin.getPreferredMfa().then((method) => {
      if (!cancelled) setMfaMethod(method);
    });
    return () => { cancelled = true; };
  }, [cognitologin]);

  const mfaEnrolled = mfaMethod === "SOFTWARE_TOKEN_MFA";
  const fileInputRef = useRef<HTMLInputElement>(null);

  const roleBadge = getRoleBadge(user.role);

  const ownedApps = useMemo(() => {
    if (!dashboardListOfWebapps) return [];
    return dashboardListOfWebapps.filter((app) => app.owner === user.emailaddress);
  }, [dashboardListOfWebapps, user.emailaddress]);

  const handleAvatarClick = useCallback(() => {
    if (!uploading) fileInputRef.current?.click();
  }, [uploading]);

  // Step 1 of the avatar flow: validate the chosen file, then hand it to the
  // crop dialog. Nothing is uploaded until the user has positioned it.
  const handleFileSelected = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAvatarError("");

    const validationResult = await validateAvatarFile(file);
    // Reset the input either way, so re-picking the same file still fires a
    // change event.
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!validationResult.ok) {
      setAvatarError(validationResult.errorMessage ?? "Invalid image");
      return;
    }

    setFileAwaitingCrop(file);
  }, []);

  // Step 2: upload the cropped result. Re-validated first, because the crop
  // produces a NEW file (re-encoded PNG) that has not been through the size,
  // MIME and magic-byte checks the originally chosen file passed.
  const handleCroppedImage = useCallback(async (croppedFile: File) => {
    setFileAwaitingCrop(null);
    setAvatarError("");

    const validationResult = await validateAvatarFile(croppedFile);
    if (!validationResult.ok) {
      setAvatarError(validationResult.errorMessage ?? "Invalid image");
      return;
    }
    const file = croppedFile;

    setUploading(true);
    try {
      // Get presigned upload URL
      const uploadUrlResponse = await API.graphql(
        graphqlOperation(getFileUploadUrl, {
          input: { fileType: "Avatar", contentType: file.type },
        })
      );
      const uploadData = getGraphData(uploadUrlResponse, "getFileUploadUrl");
      if (!uploadData?.uploadUrl || !uploadData?.fileKey) {
        setAvatarError("Failed to get upload URL");
        return;
      }

      // Upload to S3
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!uploadResponse.ok) {
        setAvatarError(`Upload failed (${uploadResponse.status})`);
        return;
      }

      // Save the S3 key as the avatar
      await API.graphql(
        graphqlOperation(changeAvatar, { input: { avatar: uploadData.fileKey } })
      );

      // Show the local preview immediately
      setAvatarSrc(URL.createObjectURL(file));
    } catch (uploadError) {
      // Logged without the raw rejection, which can carry endpoint detail.
      console.error("Avatar upload failed");
      setAvatarError("Avatar upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, []);

  const handleSelectApp = useCallback(
    (webappid: string) => {
      updateCurrentWebAppCallback(webappid);
      navigate(generatePath(SINGLE_WEBAPP_PAGE, { webappid }));
    },
    [updateCurrentWebAppCallback, navigate]
  );

  const previousIpAddress = user.previpaddress || "N/A";
  const currentIpAddress = user.currentipaddress || "N/A";
  const currentLocation =
    user.city && user.country ? `${user.city}, ${user.country}` : "N/A";
  const lastLogin = user.lastlogindate
    ? formatDateWithLocale(user.lastlogindate)
    : "N/A";

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">

        {/* Profile header card */}
        <Card className="mb-6">
          <CardContent className="flex flex-col sm:flex-row items-center gap-6 pt-6">
            {/* Avatar with upload overlay. The format hint sits in the card's
                own flow (not absolutely positioned outside it) and appears on
                hover, matching the usage cue on the Pentest card. */}
            <div className="group flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={handleAvatarClick}
                title={AVATAR_UPLOAD_HINT}
                className="relative h-24 w-24 shrink-0 rounded-full overflow-hidden ring-2 ring-gray-200 focus:outline-none focus:ring-blue-500"
                disabled={uploading}
              >
                <UserAvatar
                  avatarUrl={avatarSrc}
                  alt={`${user.given_name} ${user.family_name}`}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {uploading ? (
                    <Loader2 className="h-5 w-5 text-white animate-spin" />
                  ) : (
                    <Camera className="h-5 w-5 text-white" />
                  )}
                </div>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                accept={ALLOWED_AVATAR_MIME_TYPES.join(",")}
                onChange={handleFileSelected}
                className="sr-only"
              />
              {/* An upload error stays pinned: hiding a rejection behind hover
                  would mean the user never learns why nothing happened. The
                  format hint is the only part that waits for intent. */}
              {avatarError ? (
                <p role="alert" className="max-w-[10rem] text-center text-xs text-red-500">
                  {avatarError}
                </p>
              ) : (
                <p className="max-w-[10rem] text-center text-xs text-gray-400 opacity-0 transition-opacity group-hover:opacity-100">
                  {AVATAR_UPLOAD_HINT}
                </p>
              )}
            </div>

            {/* Name, role, then identity lines. Preferred username sits above
                the email: it is the name the user chose and the one other
                people see, so it reads as the primary identity. Each gets its
                own line rather than sharing one, so neither is truncated to
                make room for the other. */}
            <div className="min-w-0 flex-1 text-center sm:text-left">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <h1 className="text-2xl font-semibold">
                  {user.given_name} {user.family_name}
                </h1>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${roleBadge.className}`}>
                  {roleBadge.label}
                </span>
              </div>
              <div className="mt-1 flex flex-col items-center gap-1 text-base text-gray-500 sm:items-start">
                {/* Shown whenever one is set. It used to be hidden when it
                    matched the first name, which left the top line empty. */}
                {user.displayName && (
                  <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                    <UserIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="truncate">{user.displayName}</span>
                  </span>
                )}
                <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{user.emailaddress}</span>
                </span>
              </div>
            </div>

            {/* Edit button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEditModal(true)}
              className="gap-1.5 shrink-0"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit profile
            </Button>
          </CardContent>
        </Card>

        {/* Two-column layout */}
        <div className="grid gap-6 lg:grid-cols-3">

          {/* Left column — 2/3 width */}
          <div className="lg:col-span-2 flex flex-col gap-6">

            {/* Personal Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <UserIcon className="h-4 w-4 text-gray-400" />
                  Personal Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs font-medium text-gray-400 uppercase tracking-wider">Full Name</dt>
                    <dd className="mt-1 text-base">{user.given_name} {user.family_name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-400 uppercase tracking-wider">{PREFERRED_USERNAME_LABEL}</dt>
                    <dd className="mt-1 text-base">{user.displayName || "Not set"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-400 uppercase tracking-wider">Email</dt>
                    <dd className="mt-1 text-base truncate">{user.emailaddress}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-400 uppercase tracking-wider">Role</dt>
                    <dd className="mt-1 text-base">
                      {roleBadge.label}
                      {!user.isUserAdmin() && (
                        <span className="block text-xs text-gray-400 mt-0.5">
                          Contact your administrator to change
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-400 uppercase tracking-wider">Passphrase</dt>
                    <dd className="mt-1 text-base flex items-center gap-2">
                      <span className="tracking-widest">••••••••</span>
                      <button
                        type="button"
                        onClick={() => setShowEditModal(true)}
                        className="text-xs text-blue-600 hover:text-blue-500"
                      >
                        Change
                      </button>
                    </dd>
                  </div>
                  {/* Two-factor authentication — only shown once TOTP is enrolled
                      (i.e. the tenant has MFA enabled). "Replace" lets the user
                      rotate to a new authenticator/device without an admin reset. */}
                  {mfaEnrolled && (
                    <div>
                      <dt className="text-xs font-medium text-gray-400 uppercase tracking-wider">Two-Factor Auth</dt>
                      <dd className="mt-1 text-base flex items-center gap-2">
                        <span className="text-base text-emerald-700">Authenticator app</span>
                        <button
                          type="button"
                          onClick={() => setShowReplaceMfa(true)}
                          className="text-xs text-blue-600 hover:text-blue-500"
                        >
                          Replace
                        </button>
                      </dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>

            {/* Security & Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-4 w-4 text-gray-400" />
                  Security & Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-gray-100 p-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Last Login</p>
                      <p className="mt-0.5 text-base">{lastLogin}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-gray-100 p-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Current Location</p>
                      <p className="mt-0.5 text-base">{currentLocation}</p>
                    </div>
                  </div>
                  <IpAddressRow label="Current IP" ipAddress={currentIpAddress} />
                  <IpAddressRow label="Previous IP" ipAddress={previousIpAddress} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column — 1/3 width */}
          <div className="flex flex-col gap-6">

            {/* My Applications */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="h-4 w-4 text-gray-400" />
                  My Applications
                </CardTitle>
              </CardHeader>
              <CardContent>
                {ownedApps.length === 0 ? (
                  <div className="text-center py-6">
                    <Globe className="mx-auto h-8 w-8 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-400">
                      No applications registered yet
                    </p>
                    {/* Add Application affordance — admin/owner only.
                        Pentesters can't create apps (RoleGate redirects
                        /addapplication for them), so the button would
                        either be a misleading dead end or a frustrating
                        redirect loop. Hide it. */}
                    {user.isUserAdmin() && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(ADD_APP_PAGE)}
                        className="mt-3 gap-1.5"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add Application
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {ownedApps.map((app) => (
                      <button
                        key={app.id}
                        type="button"
                        onClick={() => handleSelectApp(app.id)}
                        className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left shadow-sm transition-shadow hover:shadow-md"
                      >
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white"
                          style={{ backgroundColor: app.color || deriveAppColor(app.name) }}
                        >
                          {app.abbrev || deriveAppAbbrev(app.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-base font-medium truncate">{app.name}</p>
                          <p className="text-sm text-gray-500 truncate">{app.url}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Position the chosen photo before it is uploaded. */}
      <AvatarCropModal
        open={!!fileAwaitingCrop}
        imageFile={fileAwaitingCrop}
        onCancel={() => setFileAwaitingCrop(null)}
        onCropped={handleCroppedImage}
      />

      <ChangeUserDetails
        user={user}
        cognitologin={cognitologin}
        show={showEditModal}
        close={() => setShowEditModal(false)}
        onProfileUpdated={() => setProfileRevision((revision) => revision + 1)}
      />

      {/* Self-service authenticator rotation. Re-reads the MFA method on close
          so the row stays accurate if the method changed. */}
      <ReplaceAuthenticatorModal
        show={showReplaceMfa}
        close={() => {
          setShowReplaceMfa(false);
          cognitologin.getPreferredMfa().then(setMfaMethod);
        }}
        cognitologin={cognitologin}
        accountLabel={user.emailaddress}
      />
    </div>
  );
}
